import { useState, ReactNode } from 'react';
import { ChevronDown, ChevronUp, X, Search } from 'lucide-react';
import { Button } from './Button';

interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'daterange';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

interface AdvancedSearchProps {
  fields: SearchField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSearch: () => void;
  onReset: () => void;
  children?: ReactNode;
}

export function AdvancedSearch({
  fields,
  values,
  onChange,
  onSearch,
  onReset,
  children,
}: AdvancedSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const activeFiltersCount = Object.values(values).filter(v => v !== '').length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 基本検索 */}
      <div className="flex flex-wrap gap-4 items-end">
        {children}
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className="relative"
        >
          詳細検索
          {isExpanded ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
          {!isExpanded && activeFiltersCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </div>

      {/* 詳細検索パネル */}
      {isExpanded && (
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4 rounded-lg border"
          style={{ 
            backgroundColor: 'var(--light-gray-primary)',
            borderColor: 'var(--border-color)',
          }}
        >
          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                {field.label}
              </label>
              {field.type === 'text' && (
                <input
                  type="text"
                  value={values[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                />
              )}
              {field.type === 'select' && (
                <select
                  value={values[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">すべて</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {field.type === 'date' && (
                <input
                  type="date"
                  value={values[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                />
              )}
              {field.type === 'daterange' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={values[`${field.key}Start`] || ''}
                    onChange={(e) => onChange(`${field.key}Start`, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--background-card)',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>〜</span>
                  <input
                    type="date"
                    value={values[`${field.key}End`] || ''}
                    onChange={(e) => onChange(`${field.key}End`, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--background-card)',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
              )}
            </div>
          ))}
          
          {/* アクションボタン */}
          <div className="col-span-full flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onReset} leftIcon={<X size={16} />}>
              リセット
            </Button>
            <Button type="submit" leftIcon={<Search size={16} />}>
              検索
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
